import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { isPanelAdmin } from './PublicHeader';

export default function NotFound() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [glitch, setGlitch] = useState(false);

  // Glitch effect on the 404 text
  useEffect(() => {
    const interval = setInterval(() => {
      setGlitch(true);
      setTimeout(() => setGlitch(false), 200);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const isAdminPath = location.pathname.startsWith('/admin') || isPanelAdmin(user);

  return (
    <div style={{
      minHeight: 'var(--app-vh, 100vh)',
      background: 'var(--bg-primary, #0a0a0f)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'var(--font-sans)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Subtle background grid */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: `
          linear-gradient(rgba(239, 68, 68, 0.02) 1px, transparent 1px),
          linear-gradient(90deg, rgba(239, 68, 68, 0.02) 1px, transparent 1px)
        `,
        backgroundSize: '60px 60px',
        animation: 'gridMove 20s linear infinite',
      }} />

      {/* Soft radial glow */}
      <div style={{
        position: 'absolute',
        width: '500px',
        height: '500px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(239, 68, 68, 0.05) 0%, transparent 70%)',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
      }} />

      {/* Content */}
      <div style={{
        position: 'relative',
        zIndex: 1,
        textAlign: 'center',
        padding: '2rem',
      }}>
        {/* 404 number with glitch effect */}
        <h1 style={{
          fontSize: 'clamp(6rem, 15vw, 10rem)',
          fontWeight: 900,
          margin: 0,
          lineHeight: 1,
          color: 'var(--accent-red, #ef4444)',
          textShadow: glitch
            ? '3px 0 var(--accent-cyan, #06b6d4), -3px 0 var(--accent-green, #22c55e), 0 0 40px rgba(239, 68, 68, 0.4)'
            : '0 0 40px rgba(239, 68, 68, 0.15)',
          transition: 'text-shadow 0.1s',
          letterSpacing: '-4px',
          position: 'relative',
        }}>
          404
        </h1>

        {/* Subtitle */}
        <div style={{
          fontSize: '0.9rem',
          color: 'var(--text-muted, #4a4a5a)',
          fontWeight: 600,
          letterSpacing: '6px',
          textTransform: 'uppercase',
          margin: '0.75rem 0 2rem',
        }}>
          {t('notFound.subtitle', 'Page Not Found')}
        </div>

        {/* Message */}
        <p style={{
          color: 'var(--text-secondary, #8b8b9e)',
          fontSize: '0.95rem',
          maxWidth: '400px',
          margin: '0 auto 2.5rem',
          lineHeight: 1.6,
        }}>
          {t('notFound.message', "This page doesn't exist or has been moved.")}
          <br />
          <span style={{ color: 'var(--text-muted, #4a4a5a)', fontSize: '0.85rem' }}>
            {t('notFound.routePrefix', 'The route')} <code style={{
              background: 'rgba(239, 68, 68, 0.08)',
              color: 'var(--accent-red, #ef4444)',
              padding: '2px 8px',
              borderRadius: 'var(--radius-sm, 6px)',
              fontSize: '0.8rem',
              border: '1px solid rgba(239, 68, 68, 0.12)',
            }}>{location.pathname}</code> {t('notFound.routeSuffix', 'was not found.')}
          </span>
        </p>

        {/* Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'var(--accent-blue, #3b82f6)',
              color: '#fff',
              border: 'none',
              padding: '10px 24px',
              borderRadius: 'var(--radius-md, 8px)',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontFamily: 'var(--font-sans)',
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.opacity = '0.9';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.opacity = '1';
            }}
          >
            {t('notFound.goHome', 'Go Home')}
          </button>

          {isAdminPath && (
            <button
              onClick={() => navigate('/admin/dashboard')}
              style={{
                background: 'var(--bg-card, #111118)',
                color: 'var(--text-secondary, #8b8b9e)',
                border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
                padding: '10px 24px',
                borderRadius: 'var(--radius-md, 8px)',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'var(--font-sans)',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => {
                e.target.style.background = 'var(--bg-card-hover, #16161f)';
                e.target.style.borderColor = 'var(--border-default, rgba(255,255,255,0.09))';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'var(--bg-card, #111118)';
                e.target.style.borderColor = 'var(--border-subtle, rgba(255,255,255,0.06))';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              {t('nav.dashboard', 'Dashboard')}
            </button>
          )}

          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'var(--bg-card, #111118)',
              color: 'var(--text-secondary, #8b8b9e)',
              border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
              padding: '10px 24px',
              borderRadius: 'var(--radius-md, 8px)',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-sans)',
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => {
              e.target.style.background = 'var(--bg-card-hover, #16161f)';
              e.target.style.borderColor = 'var(--border-default, rgba(255,255,255,0.09))';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'var(--bg-card, #111118)';
              e.target.style.borderColor = 'var(--border-subtle, rgba(255,255,255,0.06))';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            {t('notFound.goBack', 'Go Back')}
          </button>
        </div>
      </div>

      {/* CSS animation */}
      <style>{`
        @keyframes gridMove {
          0% { transform: translate(0, 0); }
          100% { transform: translate(60px, 60px); }
        }
      `}</style>
    </div>
  );
}
